import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getData,
  deleteData,
  ImageBaseUrl,
  postData,
} from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function SubCategoryList() {
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const rowsPerPage = 10;

  // Fetch SubCategories from API with pagination
  const fetchSubCategories = async (page = 0, search = "", status = "all") => {
    setLoading(true);
    try {
      const response = await postData("api/subcategory/list", {
        page: page + 1, // API expects 1-based index
        limit: rowsPerPage,
        // search: status === "all" ? undefined : status,
      });

      if (response?.status) {
        // Assuming 'plans' is the correct key
        setSubCategories(response?.data);
        setTotalItems(response.pagination.totalItems);
        setPageCount(response.pagination.totalPages);
      } else {
        toast.error(response?.message || "Failed to fetch sub categories");
      }
    } catch (error) {
      toast.error("Error fetching sub categories");
      console.error("Fetch sub categories error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubCategories(currentPage, searchTerm, statusFilter);
  }, [currentPage, searchTerm, statusFilter]);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page when applying filters
    fetchSubCategories(0, searchTerm, statusFilter);
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(0);
  };

  // Handle sub category deletion with SweetAlert confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData("api/subcategory/delete", {
          subcategoryId: id,
        });
        if (response.status) {
          toast.success("Sub category deleted successfully");
          fetchSubCategories(currentPage, searchTerm, statusFilter);
        } else {
          toast.error(response.message || "Failed to delete sub category");
        }
      } catch (error) {
        toast.error("Error deleting sub category");
        console.error("Delete sub category error:", error);
      }
    }
  };

  const handleStatusChange = async (subCategory) => {
    const newStatus = subCategory.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${
        newStatus === "active" ? "activate" : "deactivate"
      } this Subcategory?`,
      text: `The subcategory will be ${
        newStatus === "active" ? "activated" : "deactivated"
      }.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        newStatus === "active" ? "activate" : "deactivate"
      } it!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await postData("api/subcategory/update", {
          subcategoryId: subCategory._id,
          status: newStatus, // ✅ send correct status
        });

        if (response.status) {
          toast.success(
            `Subcategory ${
              newStatus === "active" ? "activated" : "deactivated"
            } successfully`
          );
          fetchSubCategories(currentPage, searchTerm, statusFilter); // refresh list
        } else {
          toast.error(
            response.message ||
              `Failed to ${
                newStatus === "active" ? "activate" : "deactivate"
              } Subcategory`
          );
        }
      } catch (error) {
        toast.error(
          `Error ${
            newStatus === "active" ? "activating" : "deactivating"
          } Subcategory`
        );
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Sub Category List</h4>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-subcategory")}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                Sub-category
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search Sub Categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Apply Filters
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* SubCategory Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Sub Category Name</th>
                            <th>Category Name</th>
                            {/* <th>Company Name</th> */}
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="120px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subCategories.length > 0 ? (
                            subCategories.map((subCategory, index) => (
                              <tr key={subCategory._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  {subCategory.image ? (
                                    <img
                                      src={subCategory.image}
                                      alt={subCategory.title}
                                      className="category-image"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    <div className="text-muted">No Image</div>
                                  )}
                                </td>
                                <td>{subCategory.title}</td>
                                <td>{subCategory.category?.title || "N/A"}</td>
                                {/* <td>
                                  {subCategory.company?.name}
                                </td> */}
                                <td>
                                  <span
                                    className={`badge ${
                                      subCategory.status === "active"
                                        ? "bg-success"
                                        : "bg-secondary"
                                    }`}
                                  >
                                    {subCategory.status === "active"
                                      ? "Active"
                                      : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  {new Date(
                                    subCategory.createdAt
                                  ).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/add-subcategory`, {
                                          state: {
                                            subCategoryData: subCategory,
                                          },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>

                                    <button
                                      className={`btn btn-sm btn-outline-${
                                        subCategory.status === "active"
                                          ? "warning"
                                          : "success"
                                      }`}
                                      onClick={() =>
                                        handleStatusChange(subCategory)
                                      }
                                      title={
                                        subCategory.status === "active"
                                          ? "Deactivate"
                                          : "Activate"
                                      }
                                    >
                                      <i
                                        className={`mdi mdi-${
                                          subCategory.status === "active"
                                            ? "close"
                                            : "check"
                                        }`}
                                      ></i>
                                    </button>

                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        handleDelete(subCategory._id)
                                      }
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No sub categories found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            Showing {currentPage * rowsPerPage + 1} to{" "}
                            {Math.min(
                              (currentPage + 1) * rowsPerPage,
                              totalItems
                            )}{" "}
                            of {totalItems} entries
                          </div>
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubCategoryList;
